import type { MerchantReturnPolicy, OfferShippingDetails, Organization, WebSite, WithContext } from "schema-dts";
import { Country } from "@core/schema/Country.js";
import type { DeliveryEnabledCountrySettings } from "@core/schema/CountrySettings.js";
import type { Currency } from "@core/schema/Currency.js";
import type { ExchangeRate } from "@core/schema/ExchangeRatesSetting.js";
import { fathersCompany, fathersContact } from "@core/schema/fathersCompany.js";
import { appName } from "@core/schema/names/appName.js";
import { standardReturnDays } from "@core/schema/returnPolicy.js";
import { calculatePriceWithTax } from "@core/schema/utils/calculatePriceWithTax.js";
import { convertPriceInCzkToCurrency } from "@core/schema/utils/convertPriceInCzkToCurrency.js";
import { countryToIsoCodeMap } from "@core/schema/utils/countryToIsoCodeMap.js";
import { isCountryInEuropeanUnion } from "@core/schema/utils/isCountryInEuropeanUnion.js";
import { SeoFragment } from "./SeoFragment.js";
import { seoId, shippingDetailsId } from "./seoIds.js";

function transitTimeForCountry(country: Country): { transitMin: number; transitMax: number } {
	if (country === Country.Czechia) {
		return { transitMin: 1, transitMax: 3 };
	}
	if (isCountryInEuropeanUnion(country)) {
		return { transitMin: 2, transitMax: 5 };
	}
	return { transitMin: 3, transitMax: 10 };
}

export function buildOrganization(
	webUrl: string,
	countrySettings: DeliveryEnabledCountrySettings[],
): WithContext<Extract<Organization, object>> {
	const areaServed = countrySettings.map(({ country }) => countryToIsoCodeMap[country]);
	return {
		"@context": "https://schema.org",
		"@type": "Organization",
		"@id": seoId(webUrl, SeoFragment.Organization),
		name: appName,
		legalName: fathersCompany.name,
		url: webUrl,
		logo: {
			"@type": "ImageObject",
			url: `${webUrl}/logo.png`,
			contentUrl: `${webUrl}/logo.png`,
		},
		telephone: fathersContact.phone,
		email: fathersContact.email,
		address: {
			"@type": "PostalAddress",
			streetAddress: "Mitrovická 128/66",
			postalCode: "72400",
			addressLocality: "Ostrava",
			addressCountry: "CZ",
		},
		contactPoint: {
			"@type": "ContactPoint",
			contactType: "customer service",
			telephone: fathersContact.phone,
			email: fathersContact.email,
			areaServed,
			availableLanguage: ["cs", "en"],
		},
		sameAs: fathersContact.socialProfiles,
	};
}

export function buildWebSite(webUrl: string): WithContext<WebSite> {
	return {
		"@context": "https://schema.org",
		"@type": "WebSite",
		"@id": seoId(webUrl, SeoFragment.Website),
		name: appName,
		url: webUrl,
		publisher: { "@id": seoId(webUrl, SeoFragment.Organization) },
	};
}

export function buildReturnPolicies(
	webUrl: string,
	countrySettings: DeliveryEnabledCountrySettings[],
): WithContext<MerchantReturnPolicy>[] {
	const applicableCountry = countrySettings.map(({ country }) => countryToIsoCodeMap[country]);
	return [
		{
			"@context": "https://schema.org",
			"@type": "MerchantReturnPolicy",
			"@id": seoId(webUrl, SeoFragment.ReturnPolicyPerishable),
			applicableCountry,
			returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
		},
		{
			"@context": "https://schema.org",
			"@type": "MerchantReturnPolicy",
			"@id": seoId(webUrl, SeoFragment.ReturnPolicyStandard),
			applicableCountry,
			returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
			merchantReturnDays: standardReturnDays,
			returnMethod: "https://schema.org/ReturnByMail",
			returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
		},
	];
}

export function buildShippingDetails(
	webUrl: string,
	countrySettings: DeliveryEnabledCountrySettings[],
	currency: Currency,
	exchangeRates: ExchangeRate[],
): WithContext<OfferShippingDetails>[] {
	return countrySettings.map(({ country, data: { delivery } }) => {
		const withTaxCzk = calculatePriceWithTax(delivery.flatPriceForB2c, delivery.shippingVatRate);
		const value = convertPriceInCzkToCurrency(withTaxCzk, currency, exchangeRates);
		const { transitMin, transitMax } = transitTimeForCountry(country);
		return {
			"@context": "https://schema.org",
			"@type": "OfferShippingDetails",
			"@id": shippingDetailsId(webUrl, country),
			shippingRate: {
				"@type": "MonetaryAmount",
				value: value.toFixed(2),
				currency,
			},
			shippingDestination: {
				"@type": "DefinedRegion",
				addressCountry: countryToIsoCodeMap[country],
			},
			deliveryTime: {
				"@type": "ShippingDeliveryTime",
				handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
				transitTime: { "@type": "QuantitativeValue", minValue: transitMin, maxValue: transitMax, unitCode: "DAY" },
			},
		};
	});
}
